import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { SubscriptionService } from '@/services/subscriptionService';
import { useNavigate } from 'react-router-dom';

interface SubscriptionAccess {
  hasAccess: boolean;
  status: string;
  plan: string;
  trialEndsAt: string | null;
  daysRemaining: number;
  shouldRedirectToCheckout: boolean;
  loading: boolean;
  error: string | null;
}

// Default allowed statuses - defined outside component to maintain stable reference
const DEFAULT_ALLOWED_STATUSES = ['trial_active', 'paid_active', 'active-hiveshare'];

interface UseSubscriptionAccessOptions {
  redirectToSignup?: boolean;
  allowedStatuses?: string[];
  requirePaidPlan?: boolean;
}

/**
 * Hook to manage subscription-based access control
 * Checks organization subscription status and handles redirects
 */
export const useSubscriptionAccess = (options: UseSubscriptionAccessOptions = {}) => {
  const { user, organizationId, userRole } = useAuth();
  const navigate = useNavigate();
  const [accessData, setAccessData] = useState<SubscriptionAccess>({
    hasAccess: false,
    status: 'unknown',
    plan: 'starter',
    trialEndsAt: null,
    daysRemaining: 0,
    shouldRedirectToCheckout: false,
    loading: true,
    error: null
  });

  const {
    redirectToSignup = true,
    requirePaidPlan = false
  } = options;

  // Memoize allowedStatuses to prevent infinite re-renders
  const allowedStatuses = useMemo(() => {
    return options.allowedStatuses || DEFAULT_ALLOWED_STATUSES;
  }, [options.allowedStatuses]);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user || !organizationId) {
        setAccessData(prev => ({
          ...prev,
          loading: false,
          hasAccess: false,
          shouldRedirectToCheckout: false
        }));
        return;
      }

      try {
        const accessInfo = await SubscriptionService.getOrganizationAccess(organizationId);
        
        // Apply custom access rules
        let hasAccess = allowedStatuses.includes(accessInfo.status);
        
        // If paid plan is required, block trial users
        if (requirePaidPlan && accessInfo.status === 'trial_active') {
          hasAccess = false;
        }

        setAccessData({
          hasAccess,
          status: accessInfo.status,
          plan: accessInfo.plan,
          trialEndsAt: accessInfo.trialEndsAt,
          daysRemaining: accessInfo.daysRemaining,
          shouldRedirectToCheckout: accessInfo.shouldRedirectToCheckout,
          loading: false,
          error: null
        });

        // Handle redirects for blocked access
        if (!hasAccess && redirectToSignup) {
          if (accessInfo.shouldRedirectToCheckout) {
            // Try to resume checkout first
            const resumeResult = await SubscriptionService.resumeCheckout(organizationId);
            
            if (resumeResult.success && resumeResult.redirect_url) {
              window.location.href = resumeResult.redirect_url;
              return;
            }
            
            // If resume failed, redirect to signup
            navigate('/signup');
          }
        }

      } catch (error) {
        console.error('Error checking subscription access:', error);
        setAccessData(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to check access',
          hasAccess: false
        }));
      }
    };

    checkAccess();
  }, [user, organizationId, allowedStatuses, requirePaidPlan, redirectToSignup, navigate]);

  /**
   * Manually trigger a checkout resume attempt
   */
  const resumeCheckout = async () => {
    if (!organizationId) return;

    try {
      const result = await SubscriptionService.resumeCheckout(organizationId);
      
      if (result.success && result.redirect_url) {
        window.location.href = result.redirect_url;
      } else if (result.should_create_new) {
        navigate('/signup');
      }
      
      return result;
    } catch (error) {
      console.error('Error resuming checkout:', error);
      return {
        success: false,
        message: 'Failed to resume checkout'
      };
    }
  };

  /**
   * Check if user has access to specific features based on plan
   */
  const hasFeatureAccess = (feature: string): boolean => {
    if (!accessData.hasAccess) return false;
    
    // Define feature access by plan
    const featureMatrix: Record<string, string[]> = {
      'basic_features': ['starter', 'professional', 'enterprise'],
      'advanced_reports': ['professional', 'enterprise'],
      'api_access': ['professional', 'enterprise'],
      'custom_branding': ['enterprise'],
      'priority_support': ['enterprise']
    };

    const allowedPlans = featureMatrix[feature] || [];
    return allowedPlans.includes(accessData.plan);
  };

  /**
   * Get trial countdown message for UI
   */
  const getTrialMessage = (): string | null => {
    if (accessData.status !== 'trial_active' || !accessData.daysRemaining) {
      return null;
    }

    if (accessData.daysRemaining === 1) {
      return 'Your trial expires tomorrow';
    } else if (accessData.daysRemaining <= 3) {
      return `Your trial expires in ${accessData.daysRemaining} days`;
    } else if (accessData.daysRemaining <= 7) {
      return `${accessData.daysRemaining} days left in your trial`;
    }

    return null;
  };

  return {
    ...accessData,
    resumeCheckout,
    hasFeatureAccess,
    getTrialMessage,
    isTrialExpiringSoon: accessData.status === 'trial_active' && accessData.daysRemaining <= 7,
    isPaidUser: accessData.status === 'paid_active' || accessData.status === 'active-hiveshare',
    isTrialUser: accessData.status === 'trial_active',
    isHiveShareOnly: accessData.status === 'active-hiveshare'
  };
};